import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

interface PageActionsState {
  actions: ReactNode;
  setActions: (n: ReactNode) => void;
}

const PageActionsContext = createContext<PageActionsState | null>(null);

export function PageActionsProvider({ children }: { children: ReactNode }) {
  const [actions, setActions] = useState<ReactNode>(null);
  return (
    <PageActionsContext.Provider value={{ actions, setActions }}>
      {children}
    </PageActionsContext.Provider>
  );
}

/** Consumed by AppShell to render whatever the currently mounted page set. */
export function usePageActionsSlot(): ReactNode {
  return useContext(PageActionsContext)?.actions ?? null;
}

/**
 * Drop this component inside any page to populate the TopBar's right-side
 * action slot. It registers on mount, **re-registers on every render so
 * `disabled` / `onClick` see the latest page state**, and clears on unmount.
 *
 *   function Alerts() {
 *     return (
 *       <>
 *         <PageActions>
 *           <button disabled={!dirty} onClick={handleSave}>Save</button>
 *         </PageActions>
 *         {/* page content *\/}
 *       </>
 *     );
 *   }
 */
export function PageActions({ children }: { children: ReactNode }) {
  const ctx = useContext(PageActionsContext);

  // Re-register on every children change so dynamic state inside the buttons
  // (disabled, onClick closures) stays fresh. JSX identity changes on every
  // parent render, so this effectively re-runs each render — that's intended.
  useEffect(() => {
    if (!ctx) return;
    ctx.setActions(children);
    // No cleanup here; the mount/unmount effect below clears the slot once.
  }, [ctx, children]);

  // Clear the slot only when this PageActions instance unmounts (route change).
  useEffect(() => {
    if (!ctx) return;
    return () => ctx.setActions(null);
  }, [ctx]);

  return null;
}
